import React from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  IconCart,
  NamedLink,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';
import { logSearch, SEARCH_SOURCES, SEARCH_TYPES } from '../../../util/searchLogging';

const MAX_MOBILE_SCREEN_WIDTH = 1024;
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const getInitialMobileLayout = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    // On the server we can't know viewport width. Defaulting to "desktop" avoids a
    // noticeable SSR->hydration layout flip (flash) for desktop users.
    // The correct value is set immediately on the client in the effect below.
    return false;
  }
  return window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`).matches;
};

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const Banner = ({ className, isScrolledDown }) => {
  const history = useHistory();
  const isHidden = (() => {
    try { return sessionStorage.getItem('hideBanner') === '1'; } catch (_) { return false; }
  })();
  const handleClose = () => {
    try { sessionStorage.setItem('hideBanner', '1'); } catch (_) {}
    try { const el = document.getElementById('banner'); if (el) el.style.display = 'none'; } catch (_) {}
    try { document.documentElement.style.setProperty('--bannerHeight', '0px'); } catch (_) {}
  };
  useIsomorphicLayoutEffect(() => {
    if (isHidden) {
      try { document.documentElement.style.setProperty('--bannerHeight', '0px'); } catch (_) {}
      return;
    }
    const updateHeightVar = () => {
      try {
        const el = document.getElementById('banner');
        const h = el ? el.offsetHeight : 0;
        document.documentElement.style.setProperty('--bannerHeight', `${h}px`);
      } catch (_) {}
    };
    updateHeightVar();
    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => updateHeightVar());
        const el = document.getElementById('banner');
        if (el) ro.observe(el);
      } else {
        window.addEventListener('resize', updateHeightVar);
      }
    } catch (_) {}
    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', updateHeightVar);
      } catch (_) {}
    };
  }, [isHidden]);
  if (isHidden) return null;
  
  const bannerClasses = classNames(className || css.banner, {
    [css.bannerHidden]: isScrolledDown
  });
  
  const handleBannerClick = (e) => {
    e.preventDefault();
    history.push('/promotions');
  };
  
  return (
    <div id="banner" className={bannerClasses}>
      <button 
        type="button"
        onClick={handleBannerClick} 
        className={css.bannerLink}
        aria-label="Voir les bons plans et promotions"
      >
        <span className={css.bannerEmoji} aria-hidden>🔥</span>
        <span className={css.bannerText}>Promotions exclusives : Découvrez nos meilleures offres en cliquant ici !</span>
      </button>
    </div>
  );
};

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    cartCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    delete searchParams.deals;

    // Analytics: log search event when user submits from topbar
    if (isMainSearchTypeKeywords(config)) {
      logSearch({
        term: values?.keywords,
        type: SEARCH_TYPES.KEYWORDS,
        source: SEARCH_SOURCES.TOPBAR,
      });
    } else {
      logSearch({
        term: values?.location?.search,
        type: SEARCH_TYPES.LOCATION,
        source: SEARCH_SOURCES.TOPBAR,
      });
    }

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      // User logged out
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - Inbox page now only shows 'orders' (sales moved to separate /sales page)
   * - Always use 'orders' tab for InboxPage
   */
  const topbarInboxTab = 'orders';

  const { mobilemenu, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const [isMobileLayout, setIsMobileLayout] = React.useState(getInitialMobileLayout);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
    const handleChange = event => {
      setIsMobileLayout(event.matches);
    };

    // Ensure state matches current viewport on mount
    handleChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithSlug'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
      showSearchForm={false}
      onSearchSubmit={handleSubmit}
      initialSearchFormValues={initialSearchFormValues}
      intl={intl}
      history={history}
      location={location}
      config={config}
      onCloseMenu={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
    />
  );

  // Scroll behavior for hiding/showing topbar and banner on mobile
  const [isScrolledDown, setIsScrolledDown] = React.useState(false);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const scrollRafRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !isMobileLayout) return;

    const handleScroll = () => {
      // Cancel any pending animation frame to throttle scroll handling
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }

      // Batch scroll position reads in requestAnimationFrame to prevent forced reflow
      scrollRafRef.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollThreshold = 50; // minimum scroll distance to trigger

        if (currentScrollY < lastScrollY || currentScrollY < 100) {
          // Scrolling up or near top - show topbar and banner
          setIsScrolledDown(false);
        } else if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
          // Scrolling down - hide topbar and banner
          setIsScrolledDown(true);
        }

        setLastScrollY(currentScrollY);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [lastScrollY, isMobileLayout]);

  useIsomorphicLayoutEffect(() => {
    const rafRef = { current: null };
    
    const setTopOffset = () => {
      // Cancel any pending animation frame to prevent duplicate reads
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      // Batch layout reads in requestAnimationFrame to prevent forced reflow
      rafRef.current = requestAnimationFrame(() => {
        try {
          const root = document.getElementById('topbar-root');
          const mobileContainer = document.getElementById('topbar-mobile-container');
          const h = root ? root.offsetHeight : 0;
          const mobileTopbarHeight = mobileContainer ? mobileContainer.offsetHeight : 0;
          document.documentElement.style.setProperty('--layoutTopOffset', `${h}px`);
          document.documentElement.style.setProperty(
            '--topbarOverlayOffset',
            `${mobileTopbarHeight || h}px`
          );
        } catch (_) {}
      });
    };
    
    setTopOffset();
    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => setTopOffset());
        const root = document.getElementById('topbar-root');
        if (root) ro.observe(root);
      } else {
        window.addEventListener('resize', setTopOffset);
      }
    } catch (_) {}
    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', setTopOffset);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      } catch (_) {}
    };
  }, []);

  const topbarRootClasses = classNames(classes, {
    [css.topbarScrolledDown]: isScrolledDown && isMobileLayout
  });

  const containerClasses = classNames(mobileRootClassName || css.container, mobileClassName, {
    [css.containerHidden]: isScrolledDown && isMobileLayout
  });
  const mobileSearchRowClasses = classNames(css.mobileSearchRow, {
    [css.mobileSearchRowHidden]: isScrolledDown && isMobileLayout,
  });
  const mobileSearchBarMaybe =
    showSearchForm && isMobileLayout ? (
      <div className={mobileSearchRowClasses}>
        <TopbarSearchForm
          rootClassName={css.mobileInlineSearchForm}
          onSubmit={handleSubmit}
          initialValues={initialSearchFormValues}
          isMobile
          appConfig={config}
          hidePlaceholder={true}
        />
      </div>
    ) : null;
  const mobileBannerMaybe = isMobileLayout ? (
    <Banner className={css.bannerInTopbar} isScrolledDown={isScrolledDown && isMobileLayout} />
  ) : null;
  const desktopBannerMaybe = !isMobileLayout ? (
    <Banner isScrolledDown={isScrolledDown && isMobileLayout} />
  ) : null;

  return (
    <div id="topbar-root" className={topbarRootClasses}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div id="topbar-mobile-container" className={containerClasses}>
        <Button
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon
            className={css.menuIcon}
            ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          />
        </Button>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <NamedLink name="CartPage" className={css.mobileCartButton}>
          <IconCart className={css.mobileCartIcon} size="medium" />
          {cartCount > 0 && (
            <div className={css.mobileCartBadge}>
              {cartCount > 99 ? '99+' : cartCount}
            </div>
          )}
        </NamedLink>
        {mobileSearchBarMaybe}
        {mobileBannerMaybe}
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          cartCount={cartCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
          history={history}
          routeConfiguration={routeConfiguration}
        />
      </div>
      {desktopBannerMaybe}
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        hideCloseButton={true}
      >
        {mobileMenu}
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
